import { createRng } from '../../../core/rng';
import type { Rng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, phrase, pcs, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { at: number; root: string; keys: readonly string[] };

const ORIG: Chord[][] = [
  [{ at: 0, root: 'F2', keys: ['A3', 'C4', 'E4', 'G4'] }],
  [
    { at: 0, root: 'E2', keys: ['G3', 'B3', 'D4', 'E4'] },
    { at: 8, root: 'A1', keys: ['G3', 'Bb3', 'C#4', 'E4'] },
  ],
  [{ at: 0, root: 'D2', keys: ['F3', 'A3', 'C4', 'E4'] }],
  [
    { at: 0, root: 'G1', keys: ['F3', 'A3', 'Bb3', 'D4'] },
    { at: 8, root: 'C2', keys: ['E3', 'A3', 'Bb3', 'D4'] },
  ],
  [{ at: 0, root: 'F2', keys: ['A3', 'C4', 'E4', 'G4'] }],
  [{ at: 0, root: 'Bb1', keys: ['A3', 'C4', 'D4', 'F4'] }],
  [
    { at: 0, root: 'A1', keys: ['G3', 'C4', 'E4'] },
    { at: 8, root: 'D2', keys: ['F3', 'A3', 'C4', 'E4'] },
  ],
  [
    { at: 0, root: 'G1', keys: ['F3', 'A3', 'Bb3', 'D4'] },
    { at: 8, root: 'C2', keys: ['Bb3', 'C4', 'D4', 'F4'] },
  ],
];

const ALT_A: Chord[][] = [
  [{ at: 0, root: 'Bb1', keys: ['A3', 'Bb3', 'D4', 'F4'] }],
  [
    { at: 0, root: 'A1', keys: ['G3', 'C4', 'E4'] },
    { at: 8, root: 'D2', keys: ['F#3', 'A3', 'C4', 'E4'] },
  ],
  [{ at: 0, root: 'G1', keys: ['F3', 'A3', 'Bb3', 'D4'] }],
  [{ at: 0, root: 'C2', keys: ['Bb3', 'C4', 'D4', 'F4'] }],
  [{ at: 0, root: 'F2', keys: ['A3', 'C4', 'E4', 'G4'] }],
  [{ at: 0, root: 'D2', keys: ['F3', 'A3', 'C4', 'E4'] }],
  [
    { at: 0, root: 'G1', keys: ['F3', 'Bb3', 'D4'] },
    { at: 8, root: 'C2', keys: ['E3', 'A3', 'Bb3', 'D4'] },
  ],
  [{ at: 0, root: 'F2', keys: ['A3', 'C4', 'Eb4', 'G4'] }],
];

const ALT_B: Chord[][] = [
  [{ at: 0, root: 'D2', keys: ['F3', 'A3', 'C4', 'E4'] }],
  [{ at: 0, root: 'Bb1', keys: ['A3', 'Bb3', 'D4', 'F4'] }],
  [{ at: 0, root: 'G1', keys: ['F3', 'A3', 'Bb3', 'D4'] }],
  [{ at: 0, root: 'A1', keys: ['G3', 'Bb3', 'C#4', 'E4'] }],
  [{ at: 0, root: 'D2', keys: ['F3', 'A3', 'C4', 'E4'] }],
  [
    { at: 0, root: 'F2', keys: ['A3', 'C4', 'E4', 'G4'] },
    { at: 8, root: 'C2', keys: ['Bb3', 'C4', 'D4', 'F4'] },
  ],
  [{ at: 0, root: 'Bb1', keys: ['A3', 'Bb3', 'D4', 'F4'] }],
  [
    { at: 0, root: 'E2', keys: ['G3', 'Bb3', 'D4'] },
    { at: 8, root: 'A1', keys: ['G3', 'Bb3', 'C#4', 'E4'] },
  ],
];

const MELODY: Note[][] = [
  [[0, 'E5', 4], [4, 'C5', 2], [6, 'A4', 2], [8, 'G4', 6]],
  [[0, 'G4', 2], [2, 'A4', 2], [4, 'B4', 4], [8, 'C#5', 4], [12, 'E5', 4]],
  [[0, 'F5', 6], [6, 'E5', 2], [8, 'D5', 2], [10, 'C5', 2], [12, 'A4', 4]],
  [[0, 'Bb4', 4], [4, 'A4', 2], [6, 'G4', 2], [8, 'A4', 8]],
  [[0, 'C5', 3], [3, 'A4', 1], [4, 'C5', 2], [6, 'D5', 2], [8, 'E5', 8]],
  [[0, 'F5', 4], [4, 'E5', 2], [6, 'D5', 2], [8, 'C5', 4], [12, 'D5', 4]],
  [[0, 'E5', 4], [4, 'C5', 4], [8, 'D5', 4], [12, 'F5', 4]],
  [[0, 'D5', 4], [4, 'C5', 2], [6, 'Bb4', 2], [8, 'G4', 4], [12, 'C5', 4]],
];

const KICKS = [
  ['X......x..X.....', 'X.........X..o..'],
  ['X.......X.x.....', 'X.........X.....'],
  ['X......x..X...x.', 'X.....x...X.....'],
] as const;

const KEYS = [0, -2, 2, -1, 1, 3, -3] as const;
const MAJOR = [5, 7, 9, 10, 0, 2, 4] as const;

export const CRACKLE = { density: 22, hiss: 0.012, vol: -27, rate: 44100, seconds: 4.3 } as const;

const PLAN: FormPlan = {
  intro: 4,
  outro: 8,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 1 },
    { name: 'B', bars: 8, min: 4, rank: 2 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'A2' },
    { name: 'A2', bars: 8, min: 4, rank: 3 },
  ],
};

function skipCrackle(rng: Rng): void {
  const len = Math.floor(CRACKLE.rate * CRACKLE.seconds);
  for (let i = 0; i < len; i++) {
    if (rng.float() < CRACKLE.density / CRACKLE.rate) {
      rng.float();
      rng.float();
      rng.float();
    }
    rng.float();
  }
}

function score(seed: string, p: Params): Score {
  const ch = chooser(LOFI, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick([ORIG, ALT_A, ALT_B]);
  const kicks = ch.pick(KICKS);
  const melody = ch.orig
    ? MELODY
    : phrase(
        ch.rng,
        MELODY.map(cellsOf),
        prog.map((bar) => bar.map((c) => ({ at: c.at, tones: pcs([c.root, ...c.keys]) }))),
        MAJOR,
        [67, 77],
        76,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 58, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const outroBars = form[form.length - 1]!.bars;
  const a2 = find(form, 'A2');
  const rng = createRng(`lofi-${seed}`);
  skipCrackle(rng);
  const human = (v: number) => v * (0.85 + rng.float() * 0.25);
  const drift = () => rng.range(-0.006, 0.006);
  const shift = ch.orig ? 4 : 0;

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, t: number, vel: number) => events.push({ v, t, bar, step, vel });
  const play = (v: string, bar: number, step: number, t: number, note: string, dur: number, vel: number) =>
    events.push({ v, t, bar, step, vel, dur, note });
  const chord = (bar: number, step: number, t: number, notes: readonly string[], dur: number, vel: number, strum: number) =>
    events.push({ v: 'keys', t, bar, step, vel, dur, notes, strum });

  const autos: Score['autos'] = [
    { v: 'keysLp', points: [[g.at(0), 700], [g.at(intro), 3400]] },
    { v: 'keysLp', points: [[g.at(total - outroBars / 2), 3400], [g.at(total) + 2, 500]] },
  ];

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro + shift, 8);
    const bar = prog[i]!;
    const at = (step: number) => g.at(b.bar, step);
    const drums = b.name === 'A' || b.name === 'B' || b.name === 'A2' || (b.name === 'outro' && b.index < b.bars / 2);
    const bassOn = b.name !== 'intro' && !(b.name === 'outro' && b.index >= b.bars - 2);

    for (const c of bar) {
      const span = bar.length === 1 ? 16 : 8;
      const keys = c.keys.map(tr);
      if (span === 16) {
        chord(b.bar, c.at, at(c.at) + drift(), keys, s * 10, human(0.75), 0.014);
        chord(b.bar, 11, at(11) + drift(), keys.slice(1), s * 4.5, human(0.42), 0.01);
      } else {
        chord(b.bar, c.at, at(c.at) + drift(), keys, s * 7, human(0.72), 0.014);
      }
      if (bassOn) {
        const root = tr(c.root);
        play('bass', b.bar, c.at, at(c.at), root, s * (span === 16 ? 5 : 5.5), 0.9);
        if (span === 16) {
          play('bass', b.bar, 6, at(6), transpose(root, 7), s * 2, 0.7);
          play('bass', b.bar, 10, at(10), root, s * 3, 0.8);
        }
      }
    }
    if (bassOn) {
      const next = prog[cycle(i + 1, 8)]![0]!.root;
      play('bass', b.bar, 14, at(14), tr(transpose(next, -1)), s * 1.6, 0.6);
    }

    if (drums) {
      for (const h of hits(kicks[i % 2]!)) hit('kick', b.bar, h.step, at(h.step) + drift(), human(h.vel));
      for (const h of hits('....X.......X...')) hit('snare', b.bar, h.step, at(h.step) + 0.012 + drift(), human(h.vel));
      if (i % 4 === 3) hit('snare', b.bar, 15, at(15) + drift(), 0.3);
      for (const h of hits('x-o-x-o-x-o-x-oo')) hit('hat', b.bar, h.step, at(h.step) + drift(), human(h.vel));
    }
    if (b.name === 'break') {
      for (const h of hits('....x.......x...')) hit('rim', b.bar, h.step, at(h.step) + drift(), human(h.vel));
      for (const h of hits('x-o-x-o-x-o-x-o-')) hit('hat', b.bar, h.step, at(h.step) + drift(), human(h.vel * 0.8));
    }

    const melodyOn = b.name === 'B' || b.name === 'A2' || (b.name === 'outro' && b.index < b.bars / 2);
    if (melodyOn) {
      for (const [step, note, len, vel] of melody[i]!) {
        play('flute', b.bar, step, at(step) + drift(), tr(note), len * s * 0.95, human(vel ?? 0.8));
        if (b.name === 'A2' && b.index >= b.bars / 2 && e >= 0.5) play('pluck', b.bar, step, at(step), tr(transpose(note, -12)), len * s * 0.6, human(0.6));
      }
    }
    if (b.name === 'A2' && b.index < b.bars / 2 && e >= 0.5) {
      for (const c of bar) {
        const notes = c.keys.map((n) => tr(transpose(n, 12)));
        [0, 3, 6].forEach((step, k) => {
          if (c.at + step < 16) play('pluck', b.bar, c.at + step, at(c.at + step), notes[k % notes.length]!, s * 1.5, human(0.55));
        });
      }
    }
  }

  if (a2 !== undefined) hit('kick', a2, 0, g.at(a2) - s * 0.5, 0.6);

  return {
    id: trackId(LOFI, seed, p),
    template: LOFI.id,
    seed,
    params: p,
    title: ch.orig ? LOFI.ref.title : titleOf(seed),
    genre: LOFI.genre,
    bpm: p.bpm,
    swing: 58,
    lufs: -14,
    key,
    form,
    mix: {
      reverbDecay: 2.2,
      delayTime: (60 / p.bpm) * 0.75,
      delayFeedback: 0.28,
      drive: 1.25,
      softness: 1.6,
      masterLowpass: 9000,
      pumpDepth: 0.18,
      pumpRelease: 0.35,
    },
    events,
    autos,
    accents: { kick: 'kick', snare: 'snare', keys: 'stab', flute: 'melody' },
  };
}

export const LOFI: Template = {
  id: 'lofi',
  genre: 'lo-fi hip hop',
  bpm: [70, 120],
  ref: { seed: 'chuva-no-vidro', title: 'Chuva no Vidro', params: { bpm: 78, bars: 40, energy: 0.7 } },
  score,
};
